import type {
  ContactPreference,
  ContactWindow,
  NeedsConfirmation,
  OutOfHoursConfirmation,
} from "../types/outreach.types"
import { describeWeekdays, formatHHMM, formatInTz } from "./time"

/** Shown when the portal tries to save a time outside the usual calling hours. */
export const OUT_OF_HOURS_PROMPT =
  "That's outside usual calling hours (weekdays 8am-7pm). Only book it if the patient confirmed they want a call then."

/**
 * PUT contact-preference answers 409 {detail: {error: "needs_confirmation",
 * slot, message}} for an out-of-hours time the patient has not confirmed.
 * Returns that detail, or null for any other error.
 */
export function needsConfirmation(err: any): NeedsConfirmation | null {
  if (err?.response?.status !== 409) return null
  const detail = err?.response?.data?.detail
  if (!detail || typeof detail !== "object" || detail.error !== "needs_confirmation") return null
  return {
    slot: typeof detail.slot === "string" ? detail.slot : null,
    message: typeof detail.message === "string" ? detail.message : "",
  }
}

/** The recorded out-of-hours yes, e.g. with the patient's words and when; null without one. */
export function describeConfirmation(c: OutOfHoursConfirmation | null | undefined): string | null {
  if (!c || (!c.text && !c.at)) return null
  const words = c.text?.trim() ? `"${c.text.trim()}"` : "yes"
  return `Patient confirmed this out-of-hours time: ${words}${c.at ? ` (${formatInTz(c.at)})` : ""}`
}

export function describeWindow(w: ContactWindow): string {
  const days = describeWeekdays(w.weekdays)
  if (w.start === w.end) return `${days} at ${formatHHMM(w.start)}`
  return `${days}, ${formatHHMM(w.start)} to ${formatHHMM(w.end)}`
}

function formatDate(d: string) {
  const [y, m, day] = d.split("-").map(Number)
  if (!y || !m || !day) return d
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  })
}

/** One line, plain English: "Mon, Wed at 6:00 PM · not before Sep 30 · avoid Oct 2". */
export function describePreference(p: ContactPreference | null | undefined): string {
  if (!p) return "No preference on file"
  const parts: string[] = []
  if (p.windows?.length) parts.push(p.windows.map(describeWindow).join("; "))
  if (p.not_before) parts.push(`not before ${formatDate(p.not_before)}`)
  if (p.avoid_dates?.length) parts.push(`avoid ${p.avoid_dates.map(formatDate).join(", ")}`)
  if (p.alternate_phone) parts.push(`alt. phone ${p.alternate_phone}`)
  if (p.language) parts.push(`language ${p.language}`)
  return parts.length ? parts.join(" · ") : "No preference on file"
}

/** First exact time (start == end) or window start, for prefilling a booking. */
export function suggestedTime(p: ContactPreference | null | undefined): string | null {
  const w = p?.windows?.[0]
  return w ? w.start : null
}
